import React from "react";
import { POINTS_ADMIN } from "../../config/lang";
import { translate } from "../../util/util";
import { ANSWER_TYPE_LABELS, PRIORITY_LABELS, TARGETING_GROUPS, AUDIENCE_TARGET_TYPES } from "./fields/questionOptions";

// Resolve a readable label from an FK/M2M value that may be a full object
// (name, or first/last name for users) or a bare id.
function entityLabel(v) {
  if (v === null || v === undefined) return "";
  if (typeof v !== "object") return String(v);
  if (v.name) return v.name;
  if (v.first_name || v.last_name) return `${v.first_name || ""} ${v.last_name || ""}`.trim();
  return v.label || v.title || (v.id != null ? String(v.id) : "");
}

// Per-entity targeting chips for one survey question ("All products" / "Brands: 3" / …).
function targetingChips(q, lang, G) {
  return TARGETING_GROUPS
    .map(grp => {
      if (q[grp.allKey]) return translate(G, grp.allLabelKey, lang);
      if (Array.isArray(q[grp.key]) && q[grp.key].length) return `${translate(G, grp.labelKey, lang)}: ${q[grp.key].length}`;
      return null;
    })
    .filter(Boolean);
}

function questionsView(questions, lang, G) {
  if (!Array.isArray(questions) || !questions.length) return "—";
  return (
    <div className="pt-questions-view">
      {questions.map((q, i) => (
        <div className="pt-question-view-card" key={q.id || i}>
          <div className="pt-question-view-header">
            <span className="pt-question-index">{i + 1}</span>
            <span className="pt-question-view-text">{q.text || "—"}</span>
          </div>
          <div className="pt-chips-view">
            <span className="pt-chip">{translate(G, ANSWER_TYPE_LABELS[q.answer_type], lang) || q.answer_type}</span>
            <span className="pt-chip">{translate(G, PRIORITY_LABELS[q.priority], lang)}</span>
            {q.required && <span className="pt-chip">{translate(G, "f_required", lang)}</span>}
            {targetingChips(q, lang, G).map((s, si) => <span className="pt-chip" key={si}>{s}</span>)}
          </div>
          {q.answer_type === "dropdown" && (
            <div className="pt-chips-view">
              {(q.dropdown_options || []).length
                ? q.dropdown_options.map((o, oi) => (
                  <span className="pt-chip" key={oi}>{o.label}{o.value ? ` (${o.value})` : ""}</span>
                ))
                : <span className="pt-question-view-empty">{translate(G, "no_dropdown_options", lang)}</span>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function audienceView(item, lang, G) {
  const active = AUDIENCE_TARGET_TYPES.find(t => Array.isArray(item[t.key]) && item[t.key].length);
  if (!active) return "—";
  return `${translate(G, active.labelKey, lang)}: ${item[active.key].length}`;
}

function display(field, value, lang, item) {
  if (field.type === "questions") return questionsView(value, lang, POINTS_ADMIN);
  if (field.type === "audience") return audienceView(item || {}, lang, POINTS_ADMIN);
  if (value === null || value === undefined || value === "") return "—";
  if (field.type === "checkbox") return value ? "✓" : "—";
  if (field.type === "entity") {
    if (Array.isArray(value)) {
      if (!value.length) return "—";
      // If the API returned full objects, list their names; if only ids, show the count.
      if (value.every(it => it && typeof it === "object")) {
        return (
          <span className="pt-chips-view">
            {value.map((it, i) => (
              <span className="pt-chip" key={it.id != null ? it.id : i}>{entityLabel(it)}</span>
            ))}
          </span>
        );
      }
      return `${value.length}`;
    }
    return entityLabel(value) || "—";
  }
  if (field.type === "coordinate" || field.type === "location") return value.latitude !== undefined && value.longitude !== undefined ? value.latitude + ", " + value.longitude : "—";
  if (field.type === "chips") return (value || []).join(", ") || "—";
  if (field.type === "keyvalue") {
    const entries = value && typeof value === "object" ? Object.entries(value) : [];
    return entries.length ? entries.map(([k, v]) => `${k}: ${v}`).join(", ") : "—";
  }
  if (field.type === "image") return value ? <img src={value} className="pt-image-preview" alt="" /> : "—";
  if (field.type === "textarea" && typeof value === "object") {
    const s = JSON.stringify(value);
    return s === "{}" || s === "[]" ? "—" : s;
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export default function ResourceView({ descriptor, item, lang }) {
  const G = POINTS_ADMIN;
  const Panel = descriptor.detailPanel;
  return (
    <div className="mp-view">
      <div className="mp-view-grid">
        <div className="mp-view-info-card">
          {descriptor.fields.map(f => {
            // "questions" renders a tall block; stack label-over-value full width
            // instead of the default label-left / value-right row.
            const block = f.type === "questions";
            return (
              <div className={"mp-view-info-row" + (block ? " pt-view-block-row" : "")} key={f.name}>
                <span className="mp-view-info-label">{translate(G, f.label, lang) || f.label}</span>
                <span className="mp-view-info-value">{display(f, item[f.name], lang, item)}</span>
              </div>
            );
          })}
        </div>
      </div>
      {Panel && <div className="pt-view-panel"><Panel item={item} lang={lang} /></div>}
    </div>
  );
}
